import { laravelGraphqlEndpoint } from '@/globals/globals'
import { parseJSONinObject } from '@/lib/parseJSONinObject'
import { Project } from '@/types/project'
import { Button, Flex, GridItem, SimpleGrid } from '@chakra-ui/react'
import request, { gql } from 'graphql-request'
import * as React from 'react'
import { ProjectCard } from './ProjectCard'
import { getPrefixedStorageURL } from '@/lib/getPrefixedStorageURL'

export const projectListQuery = gql`
  query getProjects($page: Int) {
    memberProjectsPaginated(first: 21, page: $page, published: true) {
      data {
        id
        images
        published
        slug
        title
        uuid
        videos
        user {
          avatar
          stripe_plan
          garages {
            public
            title
            uuid
            slug
            logo
            user {
              avatar
            }
          }
        }
      }
    }
  }
`

type ProcessProjectListProps = {
  memberProjectsPaginated: {
    data: Project[]
  }
}

export const processProjectList = (
  results: ProcessProjectListProps
): Project[] => {
  const parsedResults = results.memberProjectsPaginated.data.map(
    (project: Project): Project => parseJSONinObject(project)
  )

  // Remove projects without a title
  const filteredResults = parsedResults.filter(
    (project: Project) => project.title
  )

  // Remove duplicate titles
  const uniqueTitles = new Set()
  const uniqueResults = filteredResults.filter((project: Project) => {
    if (uniqueTitles.has(project.title)) return false
    uniqueTitles.add(project.title)
    return true
  })

  return uniqueResults
    .map((project: Project) => {
      if (!project.published) return null
      return {
        ...project,
        images: project.images
          ? [
              {
                ...project.images[0],
                pathImgkit: getPrefixedStorageURL(
                  project.images[0]?.path,
                  'imgkit'
                ),
              },
            ]
          : [],
      }
    })
    .filter(Boolean) as Project[]
}

export const getProjectsData = async ({ page }: { page?: number }) => {
  let variables = {
    page: page || 1,
    first: 21,
  }

  let results = await request(
    laravelGraphqlEndpoint,
    projectListQuery,
    variables
  )

  return processProjectList(results)
}

export const ProjectsList = ({
  projects: initialProjects,
}: {
  projects?: Project[]
}) => {
  const [projects, setProjects] = React.useState(initialProjects || [])
  const [loading, setLoading] = React.useState(false)
  const [page, setPage] = React.useState(1)

  const loadMore = () => {
    setLoading(true)
    getProjectsData({ page: page + 1 }).then(results => {
      setProjects([...projects, ...results])
      setLoading(false)
    })
    setPage(page + 1)
  }

  return (
    <>
      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={8} p={8}>
        {projects &&
          projects.length > 0 &&
          projects.map((project: Project) => (
            <GridItem key={project.uuid}>
              <ProjectCard project={project} />
            </GridItem>
          ))}
      </SimpleGrid>
      <Flex justifyContent='center' pt={12} pb={24}>
        <Button onClick={loadMore}>
          {loading ? 'Loading...' : 'Load More Projects'}
        </Button>
      </Flex>
    </>
  )
}
